import { useEffect, useRef, useState } from 'react';
import * as Cesium from 'cesium';
import 'cesium/Build/CesiumUnminified/Widgets/widgets.css';

// View rectangle constants
const WEST = 136.460;
const SOUTH = -13.985;
const EAST = 136.466;
const NORTH = -13.981;

// Modern, minimal styling theme
const THEME = {
  colors: {
    primary: '#2563eb',
    surface: '#1f2937',
    text: '#f3f4f6',
    accent: '#3b82f6',
    error: '#ef4444'
  },
  alpha: {
    overlay: 0.9,
    highlight: 0.7
  }
};

// Zone colors (muted, professional palette)
const ZONES = {
  'LMR': {
    color: Cesium.Color.fromCssColorString('#4b5563').withAlpha(0.6),
    name: 'Low-Medium Density Residential'
  },
  'CP': {
    color: Cesium.Color.fromCssColorString('#047857').withAlpha(0.6),
    name: 'Community Purposes'
  },
  'PS': {
    color: Cesium.Color.fromCssColorString('#1d4ed8').withAlpha(0.6),
    name: 'Public Space'
  },
  'OR': {
    color: Cesium.Color.fromCssColorString('#7c3aed').withAlpha(0.6),
    name: 'Organised Recreation'
  },
  'C': {
    color: Cesium.Color.fromCssColorString('#b45309').withAlpha(0.6),
    name: 'Commercial'
  },
  'default': {
    color: Cesium.Color.fromCssColorString('#6b7280').withAlpha(0.6),
    name: 'Unknown'
  }
};

// Helper function to get zone color
const getZoneColor = (zoneProperty: any): Cesium.Color => {
  if (!zoneProperty) return ZONES.default.color;
  const zoneString = zoneProperty.getValue();
  const zoneCode = zoneString.split(' ')[0];
  return ZONES[zoneCode as keyof typeof ZONES]?.color || ZONES.default.color;
};

// Custom styles
const styles = `
.keyboard-overlay {
  position: fixed;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 1rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.keyboard-overlay.visible {
  opacity: 1;
}

.keyboard-overlay kbd {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  padding: 0.125rem 0.375rem;
  font-family: ui-monospace, monospace;
  font-size: 0.75rem;
  margin-left: 0.5rem;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  pointer-events: none;
  z-index: 1100;
  white-space: nowrap;
  transition: opacity 0.2s ease;
}

.tooltip::after {
  content: '';
  position: absolute;
  border: 0.25rem solid transparent;
}

.tooltip.right {
  left: calc(100% + 0.5rem);
  top: 50%;
  transform: translateY(-50%);
}

.tooltip.right::after {
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  border-right-color: rgba(0, 0, 0, 0.8);
}

.tooltip.left {
  right: calc(100% + 0.5rem);
  top: 50%;
  transform: translateY(-50%);
}

.tooltip.left::after {
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  border-left-color: rgba(0, 0, 0, 0.8);
}

.zone-legend {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${THEME.colors.surface};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  padding: 1rem;
  color: ${THEME.colors.text};
  font-size: 0.875rem;
  max-height: 80vh;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 16rem;
}

.zone-header {
  font-weight: 500;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.zone-item {
  display: flex;
  align-items: flex-start;
  padding: 0.5rem;
  margin-bottom: 0.25rem;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.zone-item:hover,
.zone-item:focus {
  background: rgba(255, 255, 255, 0.05);
  outline: none;
}

.zone-item:focus-visible {
  box-shadow: 0 0 0 2px ${THEME.colors.primary};
}

.zone-item.highlighted {
  background: rgba(255, 255, 255, 0.1);
}

.zone-item.faded {
  opacity: 0.5;
}

.zone-color {
  width: 1rem;
  height: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  margin-right: 0.75rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.zone-details {
  flex: 1;
}

.zone-code {
  font-weight: 500;
}

.zone-count {
  margin-left: 0.5rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: normal;
}

.zone-name {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 0.125rem;
}

.reset-view-btn {
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: ${THEME.colors.surface};
  color: ${THEME.colors.text};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  z-index: 1000;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.reset-view-btn:hover,
.reset-view-btn:focus {
  background: rgba(255, 255, 255, 0.1);
  outline: none;
}

.reset-view-btn:focus-visible {
  box-shadow: 0 0 0 2px ${THEME.colors.primary};
}

.cesium-viewer {
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
}

.cesium-infoBox {
  background: ${THEME.colors.surface};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  overflow: hidden;
}

.cesium-infoBox-title {
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${THEME.colors.text};
}

.entity-details {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  color: ${THEME.colors.text};
}

.entity-details th,
.entity-details td {
  padding: 0.5rem 1rem;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.entity-details th {
  font-weight: 500;
  color: ${THEME.colors.accent};
  width: 40%;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  width: 2.5rem;
  height: 2.5rem;
  border: 2px solid ${THEME.colors.text};
  border-top-color: ${THEME.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
`;

export default function CesiumMapView() {
  const cesiumContainer = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Cesium.Viewer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [highlightedZone, setHighlightedZone] = useState<string | null>(null);
  const [zoneCounts, setZoneCounts] = useState<Record<string, number>>({});
  const [keyboardAction, setKeyboardAction] = useState<string>('');
  const keyboardTimeoutRef = useRef<number>();

  // Clear keyboard action after delay
  useEffect(() => {
    if (keyboardAction) {
      if (keyboardTimeoutRef.current) {
        window.clearTimeout(keyboardTimeoutRef.current);
      }
      keyboardTimeoutRef.current = window.setTimeout(() => {
        setKeyboardAction('');
      }, 1500);
    }
    return () => {
      if (keyboardTimeoutRef.current) {
        window.clearTimeout(keyboardTimeoutRef.current);
      }
    };
  }, [keyboardAction]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore keyboard shortcuts when typing in input fields
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      let actionText = '';

      switch (e.key) {
        case 'Escape':
          actionText = 'Reset view <kbd>Esc</kbd>';
          setKeyboardAction(actionText);
          setHighlightedZone(null);
          viewerRef.current?.camera.flyTo({
            destination: Cesium.Rectangle.fromDegrees(WEST, SOUTH, EAST, NORTH),
            orientation: {
              heading: 0.0,
              pitch: Cesium.Math.toRadians(-90),
              roll: 0.0
            },
            duration: 1.5
          });
          break;

        case 'ArrowUp':
          actionText = 'Pan north <kbd>↑</kbd>';
          setKeyboardAction(actionText);
          e.preventDefault();
          if (viewerRef.current) {
            const camera = viewerRef.current.camera;
            const heading = camera.heading;
            const pitch = camera.pitch;
            const range = camera.positionCartographic.height;
            camera.moveForward(range * 0.1);
            camera.setView({
              orientation: {
                heading: heading,
                pitch: pitch,
                roll: 0
              }
            });
          }
          break;

        case 'ArrowDown':
          actionText = 'Pan south <kbd>↓</kbd>';
          setKeyboardAction(actionText);
          e.preventDefault();
          if (viewerRef.current) {
            const camera = viewerRef.current.camera;
            const heading = camera.heading;
            const pitch = camera.pitch;
            const range = camera.positionCartographic.height;
            camera.moveBackward(range * 0.1);
            camera.setView({
              orientation: {
                heading: heading,
                pitch: pitch,
                roll: 0
              }
            });
          }
          break;

        case 'ArrowLeft':
          actionText = 'Pan west <kbd>←</kbd>';
          setKeyboardAction(actionText);
          e.preventDefault();
          if (viewerRef.current) {
            const camera = viewerRef.current.camera;
            const heading = camera.heading;
            const pitch = camera.pitch;
            const range = camera.positionCartographic.height;
            camera.moveLeft(range * 0.1);
            camera.setView({
              orientation: {
                heading: heading,
                pitch: pitch,
                roll: 0
              }
            });
          }
          break;

        case 'ArrowRight':
          actionText = 'Pan east <kbd>→</kbd>';
          setKeyboardAction(actionText);
          e.preventDefault();
          if (viewerRef.current) {
            const camera = viewerRef.current.camera;
            const heading = camera.heading;
            const pitch = camera.pitch;
            const range = camera.positionCartographic.height;
            camera.moveRight(range * 0.1);
            camera.setView({
              orientation: {
                heading: heading,
                pitch: pitch,
                roll: 0
              }
            });
          }
          break;

        case '+':
        case '=':
          actionText = 'Zoom in <kbd>+</kbd>';
          setKeyboardAction(actionText);
          e.preventDefault();
          if (viewerRef.current) {
            const camera = viewerRef.current.camera;
            const range = camera.positionCartographic.height;
            camera.zoomIn(range * 0.25);
          }
          break;

        case '-':
        case '_':
          actionText = 'Zoom out <kbd>-</kbd>';
          setKeyboardAction(actionText);
          e.preventDefault();
          if (viewerRef.current) {
            const camera = viewerRef.current.camera;
            const range = camera.positionCartographic.height;
            camera.zoomOut(range * 0.25);
          }
          break;

        case 'PageUp':
          actionText = 'Tilt up <kbd>PgUp</kbd>';
          setKeyboardAction(actionText);
          e.preventDefault();
          if (viewerRef.current) {
            const camera = viewerRef.current.camera;
            const pitch = Cesium.Math.clamp(
              camera.pitch + Cesium.Math.toRadians(10),
              -Cesium.Math.PI_OVER_TWO,
              0
            );
            camera.setView({
              orientation: {
                heading: camera.heading,
                pitch: pitch,
                roll: 0
              }
            });
          }
          break;

        case 'PageDown':
          actionText = 'Tilt down <kbd>PgDn</kbd>';
          setKeyboardAction(actionText);
          e.preventDefault();
          if (viewerRef.current) {
            const camera = viewerRef.current.camera;
            const pitch = Cesium.Math.clamp(
              camera.pitch - Cesium.Math.toRadians(10),
              -Cesium.Math.PI_OVER_TWO,
              0
            );
            camera.setView({
              orientation: {
                heading: camera.heading,
                pitch: pitch,
                roll: 0
              }
            });
          }
          break;

        case '[':
          actionText = 'Rotate left <kbd>[</kbd>';
          setKeyboardAction(actionText);
          e.preventDefault();
          if (viewerRef.current) {
            const camera = viewerRef.current.camera;
            const heading = camera.heading - Cesium.Math.toRadians(10);
            camera.setView({
              orientation: {
                heading: heading,
                pitch: camera.pitch,
                roll: 0
              }
            });
          }
          break;

        case ']':
          actionText = 'Rotate right <kbd>]</kbd>';
          setKeyboardAction(actionText);
          e.preventDefault();
          if (viewerRef.current) {
            const camera = viewerRef.current.camera;
            const heading = camera.heading + Cesium.Math.toRadians(10);
            camera.setView({
              orientation: {
                heading: heading,
                pitch: camera.pitch,
                roll: 0
              }
            });
          }
          break;

        case 'Home':
          actionText = 'Top view <kbd>Home</kbd>';
          setKeyboardAction(actionText);
          e.preventDefault();
          if (viewerRef.current) {
            const camera = viewerRef.current.camera;
            camera.setView({
              orientation: {
                heading: 0.0,
                pitch: -Cesium.Math.PI_OVER_TWO,
                roll: 0
              }
            });
          }
          break;

        // Quick zone selection with number keys
        case '1':
          actionText = `${highlightedZone === 'LMR' ? 'Unhighlight' : 'Highlight'} LMR <kbd>1</kbd>`;
          setKeyboardAction(actionText);
          setHighlightedZone(highlightedZone === 'LMR' ? null : 'LMR');
          break;
        case '2':
          actionText = `${highlightedZone === 'CP' ? 'Unhighlight' : 'Highlight'} CP <kbd>2</kbd>`;
          setKeyboardAction(actionText);
          setHighlightedZone(highlightedZone === 'CP' ? null : 'CP');
          break;
        case '3':
          actionText = `${highlightedZone === 'PS' ? 'Unhighlight' : 'Highlight'} PS <kbd>3</kbd>`;
          setKeyboardAction(actionText);
          setHighlightedZone(highlightedZone === 'PS' ? null : 'PS');
          break;
        case '4':
          actionText = `${highlightedZone === 'OR' ? 'Unhighlight' : 'Highlight'} OR <kbd>4</kbd>`;
          setKeyboardAction(actionText);
          setHighlightedZone(highlightedZone === 'OR' ? null : 'OR');
          break;
        case '5':
          actionText = `${highlightedZone === 'C' ? 'Unhighlight' : 'Highlight'} C <kbd>5</kbd>`;
          setKeyboardAction(actionText);
          setHighlightedZone(highlightedZone === 'C' ? null : 'C');
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [highlightedZone]);

  // Initialize Cesium viewer and load data
  useEffect(() => {
    // Add custom styles
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);

    const initCesium = async () => {
      try {
        // Initialize Cesium ion access token
        const ionToken = import.meta.env.VITE_CESIUM_ION_TOKEN;
        if (!ionToken) {
          throw new Error('Cesium Ion access token is missing');
        }
        Cesium.Ion.defaultAccessToken = ionToken;

        // Create viewer if container exists
        if (!cesiumContainer.current) {
          throw new Error('Cesium container not found');
        }

        // Create terrain provider
        const terrainProvider = await Cesium.createWorldTerrainAsync();

        // Create the viewer with enhanced controls
        viewerRef.current = new Cesium.Viewer(cesiumContainer.current, {
          terrainProvider,
          baseLayerPicker: false,
          timeline: false,
          animation: false,
          fullscreenButton: false,
          homeButton: false,
          geocoder: false,
          selectionIndicator: false,
          infoBox: true,
          scene3DOnly: true,
          navigationInstructionsInitiallyVisible: false,
          navigationHelpButton: false,
          sceneModePicker: false,
          projectionPicker: false,
          vrButton: false,
          contextOptions: {
            webgl: {
              alpha: false,
              depth: true,
              stencil: true,
              antialias: true,
              powerPreference: 'high-performance'
            }
          }
        });

        // Configure the globe and scene
        const scene = viewerRef.current.scene;
        scene.globe.enableLighting = true;
        scene.globe.showWaterEffect = true;

        // Configure mouse/touch interaction
        const cameraController = scene.screenSpaceCameraController;
        const handler = new Cesium.ScreenSpaceEventHandler(scene.canvas);
        
        // Add hover effect
        let hoveredEntity: Cesium.Entity | null = null;
        handler.setInputAction((movement: { endPosition: Cesium.Cartesian2 }) => {
          const pickedObject = scene.pick(movement.endPosition);
          const previousEntity = hoveredEntity;
          
          // Clear previous hover state
          if (previousEntity?.polygon) {
            previousEntity.polygon.extrudedHeight = new Cesium.CallbackProperty(() => {
              return viewerRef.current?.selectedEntity === previousEntity ? 100 : 0;
            }, false);
          }
          
          // Set new hover state
          if (pickedObject?.id?.polygon) {
            const entity = pickedObject.id;
            if (entity.polygon) {
              hoveredEntity = entity;
              entity.polygon.extrudedHeight = new Cesium.CallbackProperty(() => {
                return viewerRef.current?.selectedEntity === entity ? 100 : 50;
              }, false);
            }
          } else {
            hoveredEntity = null;
          }
        }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

        // Add click handler for entity selection
        handler.setInputAction((movement: { position: Cesium.Cartesian2 }) => {
          const pickedObject = scene.pick(movement.position);
          const entity = pickedObject?.id as Cesium.Entity | undefined;
          if (entity?.properties?.ARAC_Zone) {
            const zoneCode = entity.properties.ARAC_Zone.getValue().split(' ')[0];
            setHighlightedZone(highlightedZone === zoneCode ? null : zoneCode);
            const action = highlightedZone === zoneCode ? 'Unhighlight' : 'Highlight';
            setKeyboardAction(`${action} ${zoneCode}`);
          }
        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

        // Configure camera controller
        cameraController.enableRotate = true;
        cameraController.enableTranslate = true;
        cameraController.enableZoom = true;
        cameraController.enableTilt = true;
        cameraController.enableLook = false;
        cameraController.minimumZoomDistance = 100; // Meters
        cameraController.maximumZoomDistance = 10000; // Meters
        
        // Set touch interaction settings
        cameraController.tiltEventTypes = [
          Cesium.CameraEventType.RIGHT_DRAG,
          Cesium.CameraEventType.PINCH,
          {
            eventType: Cesium.CameraEventType.LEFT_DRAG,
            modifier: Cesium.KeyboardEventModifier.CTRL
          }
        ];
        
        cameraController.zoomEventTypes = [
          Cesium.CameraEventType.WHEEL,
          Cesium.CameraEventType.PINCH
        ];


        try {
          // Load cadastral data from Cesium Ion
          const cadastralResource = await Cesium.IonResource.fromAssetId(3080165);
          const dataSource = await Cesium.GeoJsonDataSource.load(cadastralResource, {
            clampToGround: true,
            stroke: Cesium.Color.fromCssColorString(THEME.colors.surface),
            strokeWidth: 2,
            fill: Cesium.Color.WHITE.withAlpha(0.5)
          });

          // Style entities and count zones
          const counts: Record<string, number> = {};
          dataSource.entities.values.forEach(entity => {
            if (entity.polygon) {
              // Set zone-based color
              entity.polygon.material = new Cesium.ColorMaterialProperty(
                new Cesium.CallbackProperty(() => {
                  const zoneCode = entity.properties?.ARAC_Zone?.getValue()?.split(' ')[0];
                  const color = getZoneColor(entity.properties?.ARAC_Zone);
                  if (highlightedZone && zoneCode !== highlightedZone) {
                    return color.withAlpha(0.2);
                  }
                  return color;
                }, false)
              );

              // Configure outline
              entity.polygon.outline = new Cesium.ConstantProperty(true);
              entity.polygon.outlineColor = new Cesium.ConstantProperty(
                Cesium.Color.fromCssColorString(THEME.colors.surface)
              );
              entity.polygon.outlineWidth = new Cesium.ConstantProperty(2);

              // Add selection highlight
              entity.polygon.heightReference = new Cesium.ConstantProperty(
                Cesium.HeightReference.RELATIVE_TO_GROUND
              );
              entity.polygon.height = new Cesium.ConstantProperty(0);
              entity.polygon.extrudedHeight = new Cesium.CallbackProperty(() => {
                if (viewerRef.current?.selectedEntity === entity) {
                  return 100;
                }
                return 0;
              }, false);

              // Add material highlight on hover/selection
              const defaultColor = getZoneColor(entity.properties?.ARAC_Zone);
              const material = new Cesium.ColorMaterialProperty(
                new Cesium.CallbackProperty(() => {
                  const zoneCode = entity.properties?.ARAC_Zone?.getValue()?.split(' ')[0];
                  const isHighlighted = highlightedZone && zoneCode !== highlightedZone;
                  const isSelected = viewerRef.current?.selectedEntity === entity;
                  const isHovered = hoveredEntity === entity;
                  
                  if (isSelected) {
                    return defaultColor.brighten(0.6, new Cesium.Color());
                  }
                  if (isHovered) {
                    return defaultColor.brighten(0.3, new Cesium.Color());
                  }
                  if (isHighlighted) {
                    return defaultColor.withAlpha(0.2);
                  }
                  return defaultColor;
                }, false)
              );
              entity.polygon.material = material;

              // Set entity name using Lot ID
              const lotId = entity.properties?.ARAC_Lot_Id_Database?.getValue();
              entity.name = lotId ? `Lot ${lotId}` : 'Unknown Lot';

              // Add description for info box
              const description = new Cesium.ConstantProperty(`
                <table class="entity-details" role="table" aria-label="Lot Details">
                  <tr>
                    <th scope="row">Lot ID</th>
                    <td>${entity.properties?.ARAC_Lot_Id_Database?.getValue()}</td>
                  </tr>
                  <tr>
                    <th scope="row">Zone</th>
                    <td>${entity.properties?.ARAC_Zone?.getValue()}</td>
                  </tr>
                  <tr>
                    <th scope="row">Type</th>
                    <td>${entity.properties?.ARAC_Description_Type?.getValue()}</td>
                  </tr>
                  <tr>
                    <th scope="row">Area</th>
                    <td>${entity.properties?.PAR_AREA_M?.getValue()} m²</td>
                  </tr>
                  <tr>
                    <th scope="row">Sublease Holder</th>
                    <td>${entity.properties?.ARAC_Sublease_Holder?.getValue()}</td>
                  </tr>
                  <tr>
                    <th scope="row">Status</th>
                    <td>${entity.properties?.ARAC_Sublease_Status?.getValue() || 'N/A'}</td>
                  </tr>
                </table>
              `);
              entity.description = description;

              // Count zones
              const zoneCode = entity.properties?.ARAC_Zone?.getValue()?.split(' ')[0] || 'default';
              counts[zoneCode] = (counts[zoneCode] || 0) + 1;
            }
          });

          // Update zone counts
          setZoneCounts(counts);

          // Add the data source to the viewer
          await viewerRef.current.dataSources.add(dataSource);

          // Set initial view
          const rectangle = Cesium.Rectangle.fromDegrees(WEST, SOUTH, EAST, NORTH);
          viewerRef.current.camera.flyTo({
            destination: rectangle,
            orientation: {
              heading: 0.0,
              pitch: Cesium.Math.toRadians(-90),
              roll: 0.0
            },
            duration: 0
          });

          setIsLoading(false);
        } catch (error) {
          console.error('Error loading cadastral data:', error);
          setError('Failed to load cadastral data');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error initializing Cesium:', error);
        setError(error instanceof Error ? error.message : 'Failed to initialize the 3D map');
        setIsLoading(false);
      }
    };

    initCesium();

    return () => {
      if (viewerRef.current && !viewerRef.current.isDestroyed()) {
        viewerRef.current.destroy();
      }
    };
  }, [highlightedZone]);

  return (
    <div style={{ width: '100%', height: '100vh', position: 'relative' }}>
      {isLoading && (
        <div className="loading-overlay" role="alert" aria-live="polite">
          <div className="loading-spinner" />
          <span className="sr-only">Loading map data...</span>
        </div>
      )}
      {error && (
        <div 
          role="alert"
          style={{
            position: 'absolute',
            top: '1rem',
            left: '50%',
            transform: 'translateX(-50%)',
            background: THEME.colors.error,
            color: THEME.colors.text,
            padding: '0.75rem 1rem',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            zIndex: 1000
          }}
        >
          {error}
        </div>
      )}
      {/* Reset View Button */}
      <button
        className="reset-view-btn"
        title="Reset camera view and clear zone highlight"
        aria-label="Reset view"
        onClick={() => {
          setHighlightedZone(null);
          viewerRef.current?.camera.flyTo({
            destination: Cesium.Rectangle.fromDegrees(WEST, SOUTH, EAST, NORTH),
            orientation: {
              heading: 0.0,
              pitch: Cesium.Math.toRadians(-90),
              roll: 0.0
            },
            duration: 1.5
          });
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
        </svg>
        Reset View
      </button>

      {/* Zone Legend */}
      <div 
        className="zone-legend" 
        role="region" 
        aria-label="Zone types and filters"
        title="Use number keys 1-5 to highlight zones. Arrow keys to pan, +/- to zoom, [/] to rotate, PgUp/PgDn to tilt, Home for top view, Esc to reset."
      >
        <div className="zone-header">
          <span>Zone Types</span>
          <div className="tooltip right" role="tooltip">
            Click on a zone to highlight all lots of that type
          </div>
          <span style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            {Object.values(zoneCounts).reduce((a, b) => a + b, 0)} Lots
          </span>
        </div>
        {Object.entries(ZONES).map(([code, zone]) => (
          code !== 'default' && (
            <div
              key={code}
              onClick={() => setHighlightedZone(highlightedZone === code ? null : code)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setHighlightedZone(highlightedZone === code ? null : code);
                }
              }}
              className={`zone-item ${highlightedZone === code ? 'highlighted' : ''} ${highlightedZone && highlightedZone !== code ? 'faded' : ''}`}
              role="button"
              tabIndex={0}
              aria-pressed={highlightedZone === code}
              title={`${highlightedZone === code ? 'Click to unhighlight' : `Click to highlight all ${zone.name} lots`} (${zoneCounts[code] || 0} lots)`}
            >
              <div 
                className="zone-color"
                style={{ background: zone.color.toCssColorString() }}
                aria-hidden="true"
              />
              <div className="zone-details">
                <div className="zone-code">
                  {code}
                  <span className="zone-count">({zoneCounts[code] || 0})</span>
                </div>
                <div className="zone-name">
                  {zone.name}
                </div>
              </div>
            </div>
          )
        ))}
      </div>

      {keyboardAction && (
        <div 
          className={`keyboard-overlay ${keyboardAction ? 'visible' : ''}`}
          dangerouslySetInnerHTML={{ __html: keyboardAction }}
        />
      )}
      <div
        ref={cesiumContainer}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative'
        }}
        role="application"
        aria-label="Interactive 3D map of cadastral lots"
      />
    </div>
  );
}
